<script setup lang="ts">
import { computed } from 'vue'

import { isIncidentCancelled } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { IncidentScoreConfig } from '@desktop/views/Relation/Sports/configuration'
import { injectIncidentsResultConfig } from '@desktop/views/Relation/Sports/provide-inject'

import { Result } from './types'

interface Props {
  incident: Incident
  result: Result | null
  isDeleted: boolean
}

const props = defineProps<Props>()

// inject
const incidentsResultConfig = injectIncidentsResultConfig()

// computed
const isScoreIncident = computed(() => {
  if (!incidentsResultConfig) return false

  const { scoringIncidents, oppositeTeamScoringIncidents } =
    incidentsResultConfig
  const incidentId = props.incident.incident_id
  const attributeIds = props.incident.attribute_ids
  const scoringIncidentConfig = scoringIncidents.get(incidentId)
  const oppositeTeamScoringIncidentConfig =
    oppositeTeamScoringIncidents?.get(incidentId)
  if (scoringIncidentConfig?.attributes)
    return attributeIds?.some((attributeId) =>
      isAttributeScoreAttribute(attributeId, scoringIncidentConfig),
    )
  if (oppositeTeamScoringIncidentConfig?.attributes)
    return attributeIds?.some((attributeId) =>
      isAttributeScoreAttribute(attributeId, oppositeTeamScoringIncidentConfig),
    )
  return (
    !!scoringIncidentConfig?.visible ||
    !!oppositeTeamScoringIncidentConfig?.visible
  )
})

const resultText = computed((): string | null => {
  if (!props.result) return null
  if (isIncidentCancelled(props.incident)) return ''
  if (props.isDeleted) return ''
  if (!isScoreIncident.value) return ''
  if (!incidentsResultConfig) return ''
  return `${props.result?.firstTeamResult} : ${props.result?.secondTeamResult}`
})

// methods
function isAttributeScoreAttribute(
  attributeId: number,
  scoreConfig: IncidentScoreConfig,
): boolean {
  const config = scoreConfig.attributes?.get(attributeId)
  return !!config?.visible && !!config.add
}
</script>

<template>
  <div
    v-if="resultText"
    class="text-2xs pt-0.25 font-bold"
  >
    <span>({{ resultText }})</span>
  </div>
</template>
